import { Component, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ThemeService } from '../themes/theme.service';

export interface DarkStat {
  label:      string;
  value:      string | number;
  sublabel?:  string;
  icon?:      string;       // clase PrimeIcon, ej: 'pi-users'
  iconColor?: string;
}

/** Grupo de exactamente 3 stats con su propia etiqueta */
export interface DarkStatsGroup {
  label:   string;           // ej. "Mis Objetivos" / "CEDI"
  accent?: string;           // color del pip del grupo (default: --cc-accent)
  icon?:   string;           // clase PrimeIcon sin el prefijo "pi ", ej: 'pi-user'
  stats:   [DarkStat, DarkStat, DarkStat];   // siempre exactamente 3
}

export interface DarkStatsConfig {
  title?:       string;
  headerIcon?:  string;
  headerImage?: string;
  /** Modo grupos: siempre 3 cols, cada grupo tiene su label */
  groups?: DarkStatsGroup[];
  /** Modo simple: usa cols para determinar columnas */
  cols?:   2 | 3;
  stats?:  DarkStat[];
}

@Component({
  selector: 'app-dark-stats-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './dark-stats-card.component.html',
  styleUrl:    './dark-stats-card.component.scss',
})
export class DarkStatsCardComponent {
  @Input() config!: DarkStatsConfig;

  private readonly themeSvc = inject(ThemeService);

  get cols(): number { return this.config?.cols ?? 3; }

  /** ¿Está en modo grupos? */
  get hasGroups(): boolean { return !!this.config?.groups?.length; }

  // ── % helpers ─────────────────────────────────────────────────────────────
  parsePercent(value: string | number): number | null {
    const str = String(value);
    if (!str.includes('%')) return null;
    const n = parseFloat(str.replace('%', '').trim());
    return isNaN(n) ? null : n;
  }

  isPercent(value: string | number): boolean {
    return this.parsePercent(value) !== null;
  }

  pctWidth(value: string | number): string {
    const n = this.parsePercent(value) ?? 0;
    return `${Math.min(n, 100)}%`;
  }

  pctColor(value: string | number): string {
    const t = this.themeSvc.current;
    const n = this.parsePercent(value) ?? 0;
    if (n >= 100) return t.success;
    if (n >= 80)  return t.warning;
    return t.danger;
  }

  pctGlow(value: string | number): string {
    return `0 0 12px ${this.pctColor(value)}66`;
  }

  formatValue(value: string | number): string {
    return String(value);
  }

  // ── Stat "divider right" — en modo simple ─────────────────────────────────
  isDividerRight(index: number): boolean {
    return (index + 1) % this.cols !== 0;
  }

  // ── Ícono helpers ─────────────────────────────────────────────────────────
  iconBg(color: string): string     { return `rgba(${this.hexToRgb(color)}, 0.12)`; }
  iconBorder(color: string): string  { return `1px solid rgba(${this.hexToRgb(color)}, 0.28)`; }
  iconGlow(color: string): string    { return `0 0 14px rgba(${this.hexToRgb(color)}, 0.35)`; }

  private hexToRgb(hex: string): string {
    const clean = hex.replace('#', '');
    const full  = clean.length === 3
      ? clean.split('').map(c => c + c).join('')
      : clean;
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    return `${r}, ${g}, ${b}`;
  }
}
